import React, { memo, useContext } from 'react'
import styled from 'styled-components'
import { useRouter } from 'next/router'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import Modal from '@jarvis.oddle/core/components/Modal'
import { headerHeight } from '@jarvis.oddle/core/components/Modal/Elements'
import Div from '@jarvis.oddle/core/components/Div'
import Flex from '@jarvis.oddle/core/components/Flex'
import Divider from '@jarvis.oddle/core/components/Divider'
import Image from '@jarvis.oddle/core/components/Image'
import IconClose from '@jarvis.oddle/core/components/Icons/IconClose'
import IconArrow, {
  arrowDirections
} from '@jarvis.oddle/core/components/Icons/IconArrow'
import IconGlobe from '@jarvis.oddle/core/components/Icons/IconGlobe'
import { H1, H3, P, Text } from '@jarvis.oddle/core/components/Typo'
import {
  colors,
  margin,
  marginSizes,
  mobileSideNav,
  padding,
  paddingSizes
} from '@jarvis.oddle/core/theme/default'
import { createUrl } from 'src/utils/routes'
import { pagesArray } from 'src/components/molecules/UserPagesNavigation'
import useCustomisation from 'src/hooks/useCustomisation'
import UserContext from 'src/containers/UserContext'
import { languages } from 'src/utils/localisation'
import {
  CustomModalHeader,
  GhostButton
} from 'src/components/molecules/Modals/MobileLanguageDrawer/MobileLanguageDrawer'
import useAppSelector from 'src/hooks/useAppSelector'
import { selectDetailMenuSite } from 'src/redux/meta'
import { selectUser } from 'src/redux/user'
import { selectStore } from 'src/redux/stores'
import { makeControlledModal } from 'src/decorators/ModalController'
import { NextLink } from 'src/components/molecules/ShopLink'

const Container = styled(Div)`
  position: absolute;
  top: 0;
  bottom: 0;
  height: 100%;
  width: ${mobileSideNav.width};
  background-color: ${(props) => props.backgroundColor || colors.container};
  right: 0;
`

const ScrollContainer = styled(Flex)`
  overflow-y: auto;
  width: 100%;
  height: calc(100% - ${headerHeight});
  padding: 0 ${padding[paddingSizes.small]};
`

const StyledLink = styled.a`
  width: 100%;
  text-decoration: none;
  padding: ${paddingSizes.small};
`

const MenuLink = ({ href, onClick, children, color, as }: {
  href: string
  as: string
  onClick: () => void
  color: string
  children
}) => (
  <NextLink href={href} as={as} passHref>
    <StyledLink onClick={onClick}>
      <H3 color={color}>{children}</H3>
    </StyledLink>
  </NextLink>
)

const Logo = styled(Image)`
  height: 60px;
  max-width: 180px;
`

const StoreName = styled(H1).attrs(() => ({
  bold: true,
  marginTop: marginSizes.small,
  marginBottom: marginSizes.large
}))`
  && {
    ${(props) => (props.font ? `font-family: ${props.font};` : '')}
  }
`

const LanguageButtonContainer = styled(Flex)`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  margin-bottom: ${margin[marginSizes.large]};
`

const LanguageButton = styled.button`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  border: none;
  outline: none;
  font-size: 1rem;
  font-weight: 700;
  color: ${(props) => props.color};
  background-color: transparent;
`

const StyledSpan = styled(Text).attrs(() => ({
  normalised: true
}))`
  margin-top: 6px;
`

const MobileSideNavigation = memo(
  ({
    modal,
    className,
    sectionId,
    menuName,
    src,
    srcSet,
    goToNextModal
  }: {
    modal: {
      show: boolean
      onClose: (handler?) => void
      onClosed: () => void
    }
    className: string
    sectionId: string
    menuName: string
    src: unknown
    srcSet: unknown
    goToNextModal: (modalName, props) => void
  }): JSX.Element => {
    const router = useRouter()
    const { t } = useContext(TranslationContext)
    const { login, logout, isEnabledCustomerSaveCreditCardOnStripe } =
      useContext(UserContext)
    const { locales } = useAppSelector(selectDetailMenuSite) || {}
    const { id: userId } = useAppSelector(selectUser).data
    const stores = useAppSelector(selectStore).data
    const metaState = useAppSelector(selectDetailMenuSite)

    const visibleStores = stores.filter((s) => s.showAddressOnCart)
    const showStoreInfoLink = visibleStores.length > 0
    const { getSectionFromId, previewMode } = useCustomisation()
    const { settings: headerSectionSettings = {} } =
      getSectionFromId(sectionId)
    const {
      showLogo: _showLogo,
      homepageUrl: _homepageUrl,
      showStoreName,
      storeName,
      headerBackgroundColor,
      navigationLinkFontColor,
      storeNameFontType
    } = headerSectionSettings
    const homepageUrl = createUrl(_homepageUrl)
    const name = storeName || menuName
    const showLogo = _showLogo && typeof src === 'string'
    const { reserve: enableReservationsOnShop } = metaState?.channelEnabled || {}

    const directToProductSectionMobile = () => {
      // Timeout is set to 100ms as there's a browser issue in Firefox which causes automated regression tests unable to display the product section intermittently
      setTimeout(() => {
        const navigateToProductSectionMobile = document.getElementById(
          'product-section-mobile'
        )
        navigateToProductSectionMobile &&
        navigateToProductSectionMobile.scrollIntoView(true)
      }, 100)
    }

    return (
      <Modal {...modal} slideInAnimation='right'>
        <Container
          className={className}
          backgroundColor={headerBackgroundColor}
        >
          <CustomModalHeader justifyContent='flex-end'>
            <GhostButton onClick={modal.onClose}>
              <IconClose fill={navigationLinkFontColor} />
            </GhostButton>
          </CustomModalHeader>
          <ScrollContainer>
            <Flex
              height='100%'
              width='100%'
              flexDirection='column'
              paddingLeft={paddingSizes.medium}
              paddingRight={paddingSizes.medium}
            >
              <a
                href={homepageUrl}
                onClick={(e) => {
                  if (!previewMode) return
                  e.preventDefault()
                  window.confirm(
                    t(
                      'This link cannot be opened in customisation preview. It will be opened in a new window (_target_).\nClick OK to continue',
                      { target: homepageUrl }
                    )
                  ) && window.open(homepageUrl, '_blank')
                }}
              >
                {showLogo && (
                  <div>
                    <Logo src={src} srcSet={srcSet} alt={name} />
                  </div>
                )}
                {showStoreName && (
                  <StoreName
                    color={navigationLinkFontColor}
                    font={storeNameFontType}
                  >
                    {name}
                  </StoreName>
                )}
              </a>
              <Flex flexDirection='column'>
                <MenuLink
                  href='/[locale]'
                  as={`/${router.query.locale}`}
                  onClick={() => {
                    directToProductSectionMobile()
                    modal.onClose()
                  }}
                  color={navigationLinkFontColor}
                >
                  {t('Order')}
                </MenuLink>
                {enableReservationsOnShop && (
                  <MenuLink
                    href='/[locale]/reservations'
                    as={`/${router.query.locale}/reservations`}
                    onClick={modal.onClose}
                    color={navigationLinkFontColor}
                  >
                    {t('Reservations')}
                  </MenuLink>
                )}
                {showStoreInfoLink && (
                  <MenuLink
                    href='/[locale]/stores'
                    as={`/${router.query.locale}/stores`}
                    onClick={modal.onClose}
                    color={navigationLinkFontColor}
                  >
                    {t('Stores')}
                  </MenuLink>
                )}
              </Flex>
              <Divider color={navigationLinkFontColor} />
              {userId ? (
                <React.Fragment>
                  {pagesArray
                    .filter((page) =>
                      isEnabledCustomerSaveCreditCardOnStripe
                        ? true
                        : page.id !== 'paymentMethods'
                    )
                    .map((page) => (
                      <MenuLink
                        key={page.id}
                        href={page.href}
                        as={`/${router.query.locale}${page.as}`}
                        onClick={modal.onClose}
                        color={navigationLinkFontColor}
                      >
                        {t(page.name)}
                      </MenuLink>
                    ))}
                  <MenuLink
                    href='/[locale]'
                    as={`/${router.query.locale}`}
                    onClick={() => logout({ onSuccess: modal.onClose })}
                    color={navigationLinkFontColor}
                  >
                    {t('Sign Out')}
                  </MenuLink>
                </React.Fragment>
              ) : (
                <StyledLink href={null} onClick={login}>
                  <H3 color={navigationLinkFontColor}>{t('Sign In')}</H3>
                </StyledLink>
              )}
              <LanguageButtonContainer>
                <P color={navigationLinkFontColor} normalised>
                  {t('Language')}
                </P>
                <LanguageButton
                  color={navigationLinkFontColor}
                  onClick={() => {
                    goToNextModal('MobileLanguageDrawer', {
                      showLogo: showLogo
                    })
                  }}
                >
                  <StyledSpan marginRight={marginSizes.small}>
                    <IconGlobe
                      fill={navigationLinkFontColor}
                      iconSize='medium'
                    />
                  </StyledSpan>
                  {languages[router.query.locale as string]}
                  <StyledSpan marginLeft={marginSizes.extraSmall}>
                    {locales?.length > 1 && (
                      <IconArrow
                        iconSize='medium'
                        direction={arrowDirections.right}
                        fill={navigationLinkFontColor}
                      />
                    )}
                  </StyledSpan>
                </LanguageButton>
              </LanguageButtonContainer>
            </Flex>
          </ScrollContainer>
        </Container>
      </Modal>
    )
  }
)

export default makeControlledModal(MobileSideNavigation, 'MobileSideNavigation')
